import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  FileText, 
  User, 
  Envelope, 
  Phone, 
  MapPin, 
  Image as ImageIcon, 
  CheckCircle,
  Clock,
  PaperPlaneTilt,
  SpinnerGap,
  Wrench
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';

const QuoteRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [withMaterials, setWithMaterials] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    description: '',
    photo_base64_list: []
  });
  const [submitting, setSubmitting] = useState(false);

  const getStatusStyle = (status) => {
    if (status === 'pending') return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: Clock };
    if (status === 'quote_generated') return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: CheckCircle };
    if (status === 'sent') return { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: PaperPlaneTilt };
    return { bg: 'bg-muted', text: 'text-muted-foreground', icon: FileText };
  };

  const getStatusLabel = (status) => {
    if (status === 'pending') return 'En attente';
    if (status === 'quote_generated') return 'Devis généré';
    if (status === 'sent') return 'Envoyé';
    return status;
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/quote-requests`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          photo_base64_list: [...prev.photo_base64_list, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/quote-requests`, formData);
      toast.success('Demande créée avec succès');
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        client_address: '',
        description: '',
        photo_base64_list: []
      });
      setOpen(false);
      await fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de la création de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateQuote = async (requestId) => {
    setSelectedRequest(requestId);
    setWithMaterials(true);
    setGenerateOpen(true);
  };

  const confirmGenerateQuote = async () => {
    if (!selectedRequest) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/quotes/generate`, {
        request_id: selectedRequest,
        with_materials: withMaterials
      });
      toast.success(withMaterials ? 'Devis complet généré' : 'Devis main d\'œuvre seule généré');
      setGenerateOpen(false);
      navigate(`/quotes/${response.data.quote_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du devis');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <FileText size={20} className="text-amber-600" weight="duotone" />
          </div>
          <span className="text-lg font-semibold text-foreground">Demandes</span>
          <span className="text-sm text-muted-foreground">({requests.length})</span>
        </div>
        
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button 
              className="w-10 h-10 rounded-xl bg-black dark:bg-white flex items-center justify-center transition-colors hover:opacity-90"
              data-testid="add-request-button"
            >
              <Plus size={20} className="text-white dark:text-black" weight="bold" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Nouvelle demande de devis</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Remplissez les informations du client et décrivez les travaux.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <User size={16} />
                  Nom du client
                </Label>
                <Input
                  required
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="Jean Dupont"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Envelope size={16} />
                  Email
                </Label>
                <Input
                  type="email"
                  required
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="jean@exemple.com"
                  data-testid="client-email-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Phone size={16} />
                  Téléphone
                </Label>
                <Input
                  required
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="06 12 34 56 78"
                  data-testid="client-phone-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MapPin size={16} />
                  Adresse du chantier
                </Label>
                <Input
                  required
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="123 Rue Example, 75001 Paris"
                  data-testid="client-address-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText size={16} />
                  Description des travaux
                </Label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 min-h-24 rounded-xl resize-none"
                  placeholder="Décrivez les travaux souhaités..."
                  data-testid="description-textarea"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <ImageIcon size={16} />
                  Photos (optionnel)
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="mt-1 rounded-xl"
                  data-testid="photos-input"
                />
                {formData.photo_base64_list.length > 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium mt-2 flex items-center gap-2">
                    <ImageIcon size={16} />
                    {formData.photo_base64_list.length} photo(s) sélectionnée(s)
                  </p>
                )}
              </div>
              <DialogFooter className="gap-2 pt-4">
                <button 
                  type="button" 
                  onClick={() => setOpen(false)} 
                  className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={submitting} 
                  className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black font-medium hover:opacity-90 transition-colors disabled:opacity-50"
                  data-testid="submit-request-button"
                >
                  {submitting ? 'Création...' : 'Créer la demande'}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Requests List */}
      <div className="space-y-3">
        {requests.length > 0 ? (
          requests.map((request, index) => {
            const statusStyle = getStatusStyle(request.status);
            const StatusIcon = statusStyle.icon;
            return (
              <motion.div
                key={request.request_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="premium-card p-5"
                data-testid={`request-card-${request.request_id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-semibold text-foreground">{request.client_name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${statusStyle.bg} ${statusStyle.text}`}>
                        <StatusIcon size={12} weight="fill" />
                        {getStatusLabel(request.status)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Envelope size={14} />
                        {request.client_email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone size={14} />
                        {request.client_phone}
                      </span>
                    </div>
                  </div>
                  {request.status === 'pending' && (
                    <button
                      onClick={() => handleGenerateQuote(request.request_id)}
                      className="px-4 py-2 rounded-xl bg-black dark:bg-white text-white dark:text-black text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-colors"
                      data-testid={`generate-quote-button-${request.request_id}`}
                    >
                      <FileText size={16} />
                      <span className="hidden sm:inline">Générer</span>
                    </button>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">Adresse:</span> {request.client_address}
                  </p>
                  <p className="text-muted-foreground line-clamp-2">
                    <span className="font-medium text-foreground">Description:</span> {request.description}
                  </p>
                </div>

                {request.extracted_info && (
                  <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                    <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <CheckCircle size={16} className="text-amber-600" weight="fill" />
                      Informations complémentaires
                    </p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{request.extracted_info}</p>
                  </div>
                )}
              </motion.div>
            );
          })
        ) : (
          <div className="premium-card p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <FileText size={32} className="text-muted-foreground" weight="duotone" />
            </div>
            <p className="text-foreground font-medium">Aucune demande de devis</p>
            <p className="text-sm text-muted-foreground mt-1">Créez votre première demande pour commencer</p>
          </div>
        )}
      </div>

      {/* Modal de génération de devis */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Options du devis</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Choisissez le type de devis à générer
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
              <div>
                <p className="font-medium text-foreground flex items-center gap-2">
                  <Wrench size={18} />
                  Inclure les fournitures
                </p>
                <p className="text-sm text-muted-foreground">Matériaux + Main d'œuvre + Services</p>
              </div>
              <button
                type="button"
                onClick={() => setWithMaterials(!withMaterials)}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  withMaterials ? 'bg-black dark:bg-white' : 'bg-muted'
                }`}
                data-testid="with-materials-toggle"
              >
                <span
                  className={`absolute top-1 w-6 h-6 rounded-full shadow-md transition-transform ${
                    withMaterials ? 'left-7 bg-white dark:bg-black' : 'left-1 bg-muted-foreground'
                  }`}
                />
              </button>
            </div>
            
            {!withMaterials && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Main d'œuvre seule :</strong> Le devis ne contiendra que la main d'œuvre et les services, sans les fournitures.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <button 
              onClick={() => setGenerateOpen(false)} 
              disabled={generating}
              className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors"
            >
              Annuler
            </button>
            <button 
              onClick={confirmGenerateQuote}
              disabled={generating}
              className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black font-medium hover:opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-generate-button"
            >
              {generating ? (
                <>
                  <SpinnerGap size={18} className="animate-spin" />
                  Génération...
                </>
              ) : (
                'Générer le devis'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default QuoteRequests;
